/**
 * Shared normalization utilities for matching parties and contract types.
 *
 * These functions are the single source of truth for how a party name or a
 * contract-type value is reduced to a comparison key. They are consumed by:
 * - the `@aline/database` Party/ContractType model hooks that persist a
 *   `normalizedName` column (so the DB can enforce per-org uniqueness), and
 * - the core AI extraction and relationship-matching activities that compare
 *   entities during document processing.
 *
 * Keeping them here (rather than in a server-only module) guarantees the value
 * stored in the database matches the value the application compares against.
 */

/**
 * Normalizes a document/contract-type title for comparison purposes.
 *
 * - Trims and lowercases the input
 * - Removes all punctuation, keeping only letters, numbers, and whitespace
 *
 * @param title - The title to normalize
 * @returns The normalized title, or null if the input is empty/null/undefined
 */
export function normalizeTitle(title: string | null | undefined): string | null {
  if (!title) return null;

  const lower = title.trim().toLowerCase();
  if (!lower.length) return null;

  return (
    lower
      .replace(/[^\p{L}\p{N}\s]/gu, '')
      .replace(/\s+/g, ' ')
      .trim() || null
  );
}

/**
 * A UUID's 32 hex digits are what uniquely identify it — hyphens and case
 * are purely decorative. Stripping everything else lets a mangled
 * transcription (or an e-signature envelope id printed unhyphenated in one
 * document and hyphenated in another) be compared on substance alone.
 */
export function canonicalizeUuid(value: string): string {
  return value.toLowerCase().replace(/[^0-9a-f]/g, '');
}

type Replacer = { regex: RegExp; replacement: string };

// Punctuation is stripped before these run, so dot variants are already handled.
// Optional whitespace between letters covers letter-by-letter abbreviations (e.g. "L L C" → "llc").
const PARTY_NAME_REPLACERS: Replacer[] = [
  { regex: /\bcorporation\b/g, replacement: 'corp' },
  { regex: /\bcompany\b/g, replacement: 'co' },
  { regex: /\bincorporated\b/g, replacement: 'inc' },
  { regex: /\blimited\b/g, replacement: 'ltd' },

  // Entity suffix abbreviations.
  { regex: /\bp\s*l\s*l\s*c\b/g, replacement: 'pllc' },
  { regex: /\bn\s*a\b/g, replacement: 'na' },
  { regex: /\bl\s*l\s*c\b/g, replacement: 'llc' },
  { regex: /\bl\s*l\s*p\b/g, replacement: 'llp' },
  { regex: /\bu\s*l\s*c\b/g, replacement: 'ulc' },
  { regex: /\bp\s*c\b/g, replacement: 'pc' },
  { regex: /\bl\s*p\b/g, replacement: 'lp' },
];

/**
 * Normalizes a party name for comparison purposes.
 *
 * - Trims and lowercases the input
 * - Normalizes common corporate/entity suffixes (e.g., corporation → corp, incorporated → inc, limited → ltd, l l c → llc)
 * - Removes all punctuation, keeping only letters, numbers, and whitespace
 *
 * This is a conservative normalization pass. We deliberately keep this simple
 * for now and can evolve it behind a version switch later.
 *
 * @param name - The party name to normalize
 * @returns The normalized name, or null if the input is empty/null/undefined
 */
export function normalizePartyName(name: string | null | undefined): string | null {
  if (!name) return null;

  const lower = name.trim().toLowerCase();
  if (!lower.length) return null;

  let normalized = lower.replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ');
  for (const { regex, replacement } of PARTY_NAME_REPLACERS) {
    normalized = normalized.replace(regex, replacement);
  }

  return (
    normalized
      .replace(/^the\b\s*/g, '')
      .replace(/\band\b/g, '')
      .replace(/\s+/g, ' ')
      .trim() || null
  );
}
